import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, tailwindUtils } from '@/shared/ui/utils/tailwind';
import { Form, NativeInput, Text, Title } from '@/shared/ui';
import { Button } from '@/shared/ui/components/shadcn/button';

interface Props {
  onVerified: (code: string) => void;
  token: string;
}

type ErrorKind = 'invalid' | 'rate_limited' | 'expired' | null;

/**
 * Public 4-digit access-code entry form gating the vistory approval flow.
 *
 * @param {Props} props - Verification callback and the vistory access token.
 * @returns {JSX.Element} Rendered code-entry form.
 */
export function VistoryCodeEntry({ onVerified, token }: Props) {
  const { t } = useTranslation('features/vistories');
  const [digits, setDigits] = useState(['', '', '', '']);
  const [error, setError] = useState<ErrorKind>(null);
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  function handleChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const next = [...digits];
    next[index] = value.slice(-1);
    setDigits(next);
    setError(null);
    if (value && index < 3) inputRefs.current[index + 1]?.focus();
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const code = digits.join('');
    if (code.length < 4) return;
    setLoading(true);
    setError(null);
    try {
      const { PublicVistoryService } = await import(
        '@/features/vistories/services/public-vistory-service'
      );
      const res = await PublicVistoryService.verifyCode(token, code);
      if (res.ok) {
        onVerified(code);
      } else {
        handleErrorStatus(res.status);
      }
    } catch {
      setError('invalid');
      triggerShake();
    } finally {
      setLoading(false);
    }
  }

  function handleErrorStatus(status: number) {
    if (status === 429) setError('rate_limited');
    else if (status === 410) setError('expired');
    else { setError('invalid'); triggerShake(); }
  }

  function triggerShake() {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  }

  const isComplete = digits.every((d) => d.length === 1);

  return (
    <Form onSubmit={handleSubmit} data-testid="vistory-code-entry" className="flex flex-col items-center gap-[var(--space-6)]">
      <div className="text-center">
        <Title order={1} size="xl" className="text-[var(--text-primary)]">
          {t('vistory_public.title')}
        </Title>
        <Text size="sm" className="mt-[var(--space-2)] text-[var(--text-secondary)]">
          {t('vistory_public.code_prompt')}
        </Text>
      </div>

      <div className={cn('flex gap-[var(--space-3)]', shake && 'animate-[shake_0.4s_ease-in-out]')}>
        {digits.map((digit, i) => (
          <NativeInput
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            aria-label={t('vistory_public.digit_label', { position: i + 1 })}
            data-testid={`vistory-code-input-${i}`}
            className={cn(
              tailwindUtils.input,
              'h-14 w-12 text-center text-[length:var(--text-2xl)] font-bold',
              error === 'invalid' && 'border-[var(--status-error)]',
            )}
          />
        ))}
      </div>

      {error && (
        <Text size="sm" className="text-[var(--status-error)]">
          {t(`vistory_public.error_${error}`)}
        </Text>
      )}

      <Button type="submit" disabled={!isComplete || loading} className="w-full max-w-[200px]">
        {loading ? t('vistory_public.verifying') : t('vistory_public.verify_button')}
      </Button>
    </Form>
  );
}
